import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const AdvancedSettings = ({ userRole, onSave }) => {
  const { user } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [exportInProgress, setExportInProgress] = useState(false);

  const handleExportData = async () => {
    if (!user) return;

    try {
      // Export user data from Supabase
      const exportData = {
        user: user,
        exportDate: new Date()?.toISOString(),
        message: 'Data export from live Supabase database'
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blind-data-export-${Date.now()}.json`;
      a?.click();
      URL.revokeObjectURL(url);

      onSave?.('Data exported successfully');
    } catch (err) {
      console.error('Error exporting data:', err);
    }
  };

  const handleDeactivateAccount = () => {
    if (deleteConfirmText?.toLowerCase() === 'delete my account') {
      // In a real app, this would call an API to deactivate the account
      alert('Account deactivation requested. You will be logged out.');
      // Removed: userSession?.clear(); - userSession is not defined
      window.location.href = '/';
    }
  };

  const handleChangePassword = () => {
    alert('Password change functionality would redirect to a secure password change flow');
  };

  const handleManageConnectedDevices = () => {
    alert('This would show a list of devices where you\'re logged in with options to revoke access');
  };

  const handleViewAuditLog = () => {
    alert('This would show your account activity history and security events');
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-2">Advanced Settings</h2>
        <p className="text-sm text-[#6B7280]">
          Manage account security, data export, and account deactivation
        </p>
      </div>

      {/* Account Security */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Lock" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Account Security</h3>
        </div>
        
        <div className="space-y-3">
          <Button
            onClick={handleChangePassword}
            variant="outline"
            iconName="Key"
            className="w-full justify-start"
          >
            Change Password
          </Button>

          <Button
            onClick={handleManageConnectedDevices}
            variant="outline"
            iconName="Smartphone"
            className="w-full justify-start"
          >
            Manage Connected Devices
          </Button>

          <Button
            onClick={handleViewAuditLog}
            variant="outline"
            iconName="FileText"
            className="w-full justify-start"
          >
            View Account Activity Log
          </Button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
          <Icon name="Info" size={20} color="#3b82f6" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Security Tip</p>
            <p className="text-blue-700">
              Enable two-factor authentication in Privacy Controls for enhanced account security.
            </p>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Database" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Data Management</h3>
        </div>
        
        {!showExportConfirm ? (
          <Button
            onClick={() => setShowExportConfirm(true)}
            variant="outline"
            iconName="Download"
            className="w-full justify-start"
          >
            Export My Data
          </Button>
        ) : (
          <div className="bg-white border-2 border-[#5B2DFF] rounded-xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Icon name="Download" size={24} color="#5B2DFF" />
              <div>
                <h4 className="font-semibold text-[#111827] mb-2">Export Your Data</h4>
                <p className="text-sm text-[#6B7280] mb-4">
                  This will create a JSON file containing all your profile information, preferences, and activity data.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={handleExportData}
                    loading={exportInProgress}
                    iconName="Download"
                    className="bg-gradient-to-r from-[#5B2DFF] to-[#7C3FFF]"
                  >
                    {exportInProgress ? 'Exporting...' : 'Confirm Export'}
                  </Button>
                  <Button
                    onClick={() => setShowExportConfirm(false)}
                    variant="outline"
                    disabled={exportInProgress}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <Icon name="AlertCircle" size={20} color="#f59e0b" />
          <div className="text-sm text-amber-900">
            <p className="font-medium mb-1">Data Privacy</p>
            <p className="text-amber-700">
              Your exported data will contain sensitive information. Store it securely and never share it publicly.
            </p>
          </div>
        </div>
      </div>

      {/* Account Deactivation */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-red-200">
          <Icon name="AlertTriangle" size={20} color="#dc2626" />
          <h3 className="text-lg font-semibold text-red-600">Danger Zone</h3>
        </div>
        
        {!showDeleteConfirm ? (
          <Button
            onClick={() => setShowDeleteConfirm(true)}
            variant="outline"
            iconName="Trash2"
            className="w-full justify-start border-red-300 text-red-600 hover:bg-red-50"
          >
            Deactivate Account
          </Button>
        ) : (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={24} color="#dc2626" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-900 mb-2">Deactivate Your Account</h4>
                <p className="text-sm text-red-700 mb-4">
                  This action will permanently delete your account and all associated data. This cannot be undone.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm font-medium text-red-900 mb-3">What will be deleted:</p>
                    <ul className="space-y-2 text-sm text-red-700">
                      <li className="flex items-center gap-2">
                        <Icon name="X" size={16} color="#dc2626" />
                        Your profile and all personal information
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="X" size={16} color="#dc2626" />
                        All matches and conversations
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="X" size={16} color="#dc2626" />
                        Saved preferences and settings
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="X" size={16} color="#dc2626" />
                        Account history and activity logs
                      </li>
                    </ul>
                  </div>

                  <Input
                    label='Type "DELETE MY ACCOUNT" to confirm'
                    placeholder="DELETE MY ACCOUNT"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e?.target?.value)}
                  />

                  <div className="flex gap-3">
                    <Button
                      onClick={handleDeactivateAccount}
                      disabled={deleteConfirmText?.toLowerCase() !== 'delete my account'}
                      variant="danger"
                      iconName="Trash2"
                    >
                      Permanently Delete Account
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help & Support */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="HelpCircle" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Help & Support</h3>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <Icon name="MessageCircle" size={24} color="#5B2DFF" />
            <div>
              <h4 className="font-semibold text-[#111827] mb-2">Need Help?</h4>
              <p className="text-sm text-[#6B7280] mb-4">
                Our support team is here to help with any questions about your account or preferences.
              </p>
              <Button
                variant="outline"
                iconName="Mail"
                onClick={() => alert('This would open a support contact form or email client')}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;